import React from "react";

import classnames from "classnames";
import {
  findDependentCostforSelectedPlan,
  calculateCompanyDependentCost,
  calculateEmployeeDependentCost,
} from "EmployerApplication/Components/ComprehensiveCostEstimation/CostCalculations";
import formatCurrency from "Utils/formatCurrency";

import { TR, TD } from "Cobalt/TableV2";
import BodyText from "Common/BodyText";
import { CONTRIBUTION_TYPES } from "Common/Constants";
import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("CostBreakdown");

const DependentRow = ({
  index,
  employee,
  selectedPlan,
  contributionType,
  contribution,
  hasTieredContributions,
  employeeIndex,
  is_enrolling,
  medicalPlans,
  dependent,
  noEmployeeEnrollments,
  is_enrolling_dental,
  is_enrolling_vision,
  dentalPlan,
  visionPlan,
  stripedRow,
}) => {
  const { name: dependentName, age } = dependent;

  const employeeDepentCost = formatCurrency(
    calculateEmployeeDependentCost(
      employee,
      age,
      dependentName,
      selectedPlan,
      dentalPlan,
      visionPlan,
      {
        is_enrolling,
        is_enrolling_dental,
        is_enrolling_vision,
      },

      contributionType,
      contribution,
      employeeIndex,
      index,
      medicalPlans,
    ),
  );

  const companyDependentCost = formatCurrency(
    calculateCompanyDependentCost(
      contributionType,
      contribution,
      dependentName,
      age,
      employee,
      selectedPlan,
      dentalPlan,
      visionPlan,
      {
        is_enrolling,
        is_enrolling_dental,
        is_enrolling_vision,
      },

      employeeIndex,
      index,
      medicalPlans,
    ),
  );

  const dependentCost = formatCurrency(
    findDependentCostforSelectedPlan(
      dependentName,
      age,
      employee,
      selectedPlan,
      dentalPlan,
      visionPlan,
      {
        is_enrolling,
        is_enrolling_dental,
        is_enrolling_vision,
      },

      employeeIndex,
      index,
    ),
  );

  const enrollmentDecisionCompleted = employee.is_enrolling !== undefined;

  const dentalPlanOffered = dentalPlan !== undefined && dentalPlan !== null;
  const visionPlanOffered = visionPlan !== undefined && visionPlan !== null;
  const columnSpan = [dentalPlanOffered, visionPlanOffered].filter((x) => x).length + 2;

  return (
    <TR
      index={`employee${employeeIndex}dependent${index}`}
      className={classnames(bem("EmployeeDetailsRow"), {
        "BEMTable__Row--striped": stripedRow,
      })}
    >
      <TD className={bem("EmployeeDetailsColumn")}>&nbsp;&nbsp;&nbsp;&nbsp;{dependentName}</TD>
      <TD className={bem("EmployeeDetailsColumnThin")}>{age}</TD>
      {!noEmployeeEnrollments && (
        <>
          {hasTieredContributions && <TD>{/* tier spacer */}</TD>}
          {enrollmentDecisionCompleted && (
            <TD className={classnames(bem("EmployeeDetailsColumn"))}>
              {is_enrolling ? (
                <>
                  <Icon icon="check" />
                  <span className="sr-only">Enrolling in medical</span>
                </>
              ) : (
                <>
                  <Icon icon="ban" />
                  <span className="sr-only">Not enrolling in medical</span>
                </>
              )}
            </TD>
          )}

          {!enrollmentDecisionCompleted && (
            <TD className={classnames(bem("EmployeeDetailsColumn"))} colSpan={columnSpan}>
              <BodyText>Awaiting enrollment decision</BodyText>
            </TD>
          )}

          {enrollmentDecisionCompleted && (
            <>
              {dentalPlanOffered && (
                <TD className={bem("EmployeeDetailsColumnThin")}>
                  {is_enrolling_dental !== undefined ? (
                    is_enrolling_dental ? (
                      <>
                        <Icon icon="check" />
                        <span className="sr-only">Enrolling in dental</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="ban" />
                        <span className="sr-only">Not enrolling in dental</span>
                      </>
                    )
                  ) : null}
                </TD>
              )}

              {visionPlanOffered && (
                <TD className={bem("EmployeeDetailsColumnThin")}>
                  {is_enrolling_vision !== undefined ? (
                    is_enrolling_vision ? (
                      <>
                        <Icon icon="check" />
                        <span className="sr-only">Enrolling in vision</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="ban" />
                        <span className="sr-only">Not enrolling in vision</span>
                      </>
                    )
                  ) : null}
                </TD>
              )}
            </>
          )}

          {contributionType !== CONTRIBUTION_TYPES.fixed &&
          selectedPlan?.pricing_type !== "segment" &&
          contributionType !== CONTRIBUTION_TYPES.percent_of_base_plan &&
          enrollmentDecisionCompleted ? (
            <>
              <TD align="right">{employeeDepentCost === "$NaN" ? "$0" : employeeDepentCost}</TD>
              <TD align="right">{companyDependentCost === "$NaN" ? "$0" : companyDependentCost}</TD>
              <TD align="right">{dependentCost === "$NaN" ? "$0" : dependentCost}</TD>
            </>
          ) : (
            <>
              <TD align="right">{/* individual cost spacer */}</TD>
              <TD align="right">{/* company cost spacer */}</TD>
              <TD align="right">{/* total cost spacer */}</TD>
            </>
          )}
        </>
      )}
    </TR>
  );
};

const DependentsTable = (props) => {
  const { employee } = props;
  const dependents = employee.children.map((childAge, index) => {
    const childEnrollment = employee.children_enrollment[index];
    return {
      name: "Child",
      age: childAge,
      ...childEnrollment,
    };
  });

  if (employee.spouse_age) {
    const spouse = {
      name: "Spouse",
      age: employee.spouse_age,
      ...employee.spouse_enrollment,
    };

    dependents.unshift(spouse);
  }
  return (
    <>
      {dependents.map((dependent, index) => (
        <DependentRow
          key={index}
          dependent={dependent}
          index={index}
          employee={props.employee}
          employeeIndex={props.employeeIndex}
          dentalPlan={props.dentalPlan}
          visionPlan={props.visionPlan}
          selectedPlan={props.selectedPlan}
          contributionType={props.contributionType}
          contribution={props.contribution}
          noEmployeeEnrollments={props.noEmployeeEnrollments}
          hasTieredContributions={props.hasTieredContributions}
          stripedRow={props.stripedRow}
          is_enrolling={dependent.is_enrolling}
          is_enrolling_dental={dependent.is_enrolling_dental}
          is_enrolling_vision={dependent.is_enrolling_vision}
          medicalPlans={props.medicalPlans}
        />
      ))}
    </>
  );
};

export default DependentsTable;
